import React, {FC, useState} from 'react';

//MUI
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';

//MUI-ICONS
import MapIcon from '@mui/icons-material/Map';
import MenuIcon from '@mui/icons-material/Menu';
import LayersIcon from '@mui/icons-material/Layers';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import BarChartIcon from '@mui/icons-material/BarChart';
import LogoutIcon from '@mui/icons-material/Logout';

//GEPROCAR
import GeprocarIsotipoBlanco from './logos/GeprocarIsotipoBlanco';
import InfoMenu from './NavigationMenu/InfoMenu';

import ResponsiveHeader from '@geomatico/geocomponents/Layout/ResponsiveHeader';
import SidePanel from '@geomatico/geocomponents/Layout/SidePanel';
import MiniSidePanel from '@geomatico/geocomponents/Layout/MiniSidePanel';

//UTILS
import styled from '@mui/system/styled';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  DRAWER_WIDTH, MAIN_CONTENT_ID,
  MINI_SIDE_PANEL_DENSE_WIDTH,
  SIDEPANEL_CONTENT_ID,
  SM_BREAKPOINT
} from '@/config';
import {Profile} from '@/domain/entities/Profile';
import {useTranslation} from 'react-i18next';
import {SxProps} from '@mui/system/styleFunctionSx/styleFunctionSx';
import {useNavigate} from 'react-router-dom';
import {CircularProgress, Tooltip} from '@mui/material';

export type MainProps = {
  widescreen: boolean,
  isSidePanelOpen: boolean
}

const Main = styled(Box)<MainProps>(() => ({
  flexGrow: 1,
  padding: 0,
  position: 'absolute',
  top: 56,
  '@media (min-width: 0px) and (orientation: landscape)': {
    top: 48
  },
  ['@media (min-width: '+ SM_BREAKPOINT +'px)']: {
    top: 64
  },
  bottom: 0,
  right: 0,
  left: MINI_SIDE_PANEL_DENSE_WIDTH + DRAWER_WIDTH
})) as React.ElementType;

const responsiveHeaderSx = {
  '&.MuiAppBar-root': {
    zIndex: 1500
  },
  '& .ResponsiveHeader-title': {
    fontSize: '32px',
    fontWeight: 'bolder'
  },
  '& .ResponsiveHeader-startIcon': {
    ml: 1
  },
};

const miniSidePanelSx = {
  '& .MiniSidePanel-itemSelected:hover': {
    backgroundColor: 'primary.main'
  },
};

const mainContentIconSx: SxProps = {
  position: 'absolute',
  top: 20,
  right: 20,
  zIndex: 1000,
  bgcolor: 'primary.main',
  color: 'common.white',
  opacity: 0.75,
  '&:hover': {
    bgcolor: 'secondary.main'
  }
};

export type LayoutProps = {
  sidePanelContentId: string,
  mainContentId: string,
  mainContent: React.ReactElement,
  profile: Profile | undefined,
  isLoading: boolean,
  sidePanelContent: React.ReactElement,
  onActionIdChange: (action: string) => void,
  onContentActionClick: () => void
};

const Layout:  FC<LayoutProps> = ({
  sidePanelContentId = SIDEPANEL_CONTENT_ID.FILTERS,
  mainContentId = MAIN_CONTENT_ID.MAP,
  mainContent,
  profile,
  sidePanelContent,
  isLoading,
  onActionIdChange,
  onContentActionClick
}) => {

  const {t} = useTranslation();
  const navigate = useNavigate();

  const widescreen = useMediaQuery(`@media (min-width:${SM_BREAKPOINT}px)`, {noSsr: true});
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(true);
  const [isNavigationMenuOpen, setNavigationMenuOpen] = useState(false);

  const sidePanelSx: SxProps = {
    '& .MuiPaper-root': {
      left: MINI_SIDE_PANEL_DENSE_WIDTH - 1
    }
  };

  const centerSx: SxProps = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)'
  };

  //LAYOUT
  const handleClose = () => setIsSidePanelOpen(!isSidePanelOpen);
  const handleNavigationOpen = () => setNavigationMenuOpen(true);
  const handleNavigationMenuClose = () => setNavigationMenuOpen(!isNavigationMenuOpen);

  const handleActionClick = (actionId: string) =>
    actionId && onActionIdChange(actionId);

  const ACTIONS = [
    {
      id: SIDEPANEL_CONTENT_ID.FILTERS,
      visible: true,
      label:  t(`actions.${SIDEPANEL_CONTENT_ID.FILTERS}`),
      content: <FilterAltIcon/>,
      disabled: false,
      action: () => onActionIdChange(SIDEPANEL_CONTENT_ID.FILTERS)
    },
    {
      id: SIDEPANEL_CONTENT_ID.LAYERS,
      visible: true,
      label:  t(`actions.${SIDEPANEL_CONTENT_ID.LAYERS}`),
      content: <LayersIcon/>,
      disabled: false,
      action: () => onActionIdChange(SIDEPANEL_CONTENT_ID.LAYERS)
    },
  ];

  const PROFILE_DEFAULT: Profile = {
    first_name: '',
    last_name: '',
    uuid: '',
    email: '',
    nif: '',
    phone: '',
    address: '',
    role: ''
  };

  const PROFILE = profile || PROFILE_DEFAULT;

  const handleLogout = () => {
    localStorage.removeItem('clas.accessToken');
    localStorage.removeItem('clas.refreshToken');
    navigate('/login/');
  };

  return <>
    <ResponsiveHeader
      title='Geprocar'
      logo={ <GeprocarIsotipoBlanco style={{margin: '0 20px'}}/> }
      startIcon={<MenuIcon/>}
      onStartIconClick={widescreen ? handleNavigationOpen : handleClose}
      isStartIconCloseable={false}
      sx={responsiveHeaderSx}
    >
      <Tooltip title={t('login.logout') ?? ''}>
        <LogoutIcon sx={{cursor: 'pointer'}} onClick={() => handleLogout()}/>
      </Tooltip>
    </ResponsiveHeader>
    <MiniSidePanel
      actions={ACTIONS}
      selectedActionId={sidePanelContentId}
      onActionClick={handleActionClick}
      dense={true}
      sx={miniSidePanelSx}
    />
    {sidePanelContent && isSidePanelOpen &&
      <SidePanel
        drawerWidth={DRAWER_WIDTH + 'px'}
        anchor="left"
        isOpen={isSidePanelOpen}
        onClose={handleClose}
        widescreen={widescreen}
        sx={sidePanelSx}
      >
        {sidePanelContent}
      </SidePanel>
    }
    <Main widescreen={widescreen} isSidePanelOpen={isSidePanelOpen}>
      {!isLoading && <IconButton onClick={onContentActionClick} sx={mainContentIconSx}>
        {
          mainContentId === MAIN_CONTENT_ID.MAP
            ? <BarChartIcon/>
            : <MapIcon/>
        }
      </IconButton>}

      {mainContent}
    </Main>
    {isLoading &&  <CircularProgress color='secondary' size={48} sx={centerSx}/>}
    <InfoMenu
      isInfoMenuOpen={isNavigationMenuOpen}
      profile={PROFILE}
      onClose={handleNavigationMenuClose}
    />
  </>;
};

export default Layout;
